import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Loader2, Package, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CLR = {
  low: "border-l-white/20", medium: "border-l-sky-500", high: "border-l-amber-500", critical: "border-l-red-500",
};
const COL_ACCENT = {
  planned: "text-white/60", waiting_parts: "text-amber-400", parts_received: "text-sky-400",
  scheduled: "text-violet-400", in_progress: "text-orange-400", completed: "text-emerald-400",
};

export default function MaintenanceBoard({ token, onOpenCard, refreshKey }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fleet/maintenance-board`, auth);
      setBoard(data);
    } catch {
      toast.error("Failed to load board");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const newStatus = destination.droppableId;
    // optimistic update
    const prev = board;
    const card = board.columns[source.droppableId].find((c) => c.id === draggableId);
    const next = { ...board, columns: { ...board.columns } };
    next.columns[source.droppableId] = board.columns[source.droppableId].filter((c) => c.id !== draggableId);
    next.columns[newStatus] = [{ ...card, status: newStatus }, ...board.columns[newStatus]];
    next.counts = { ...board.counts, [source.droppableId]: next.columns[source.droppableId].length, [newStatus]: next.columns[newStatus].length };
    setBoard(next);
    try {
      await axios.patch(`${API}/work-orders/${draggableId}/status`, { status: newStatus }, auth);
      toast.success(`Moved to ${board.statuses.find((s) => s.key === newStatus)?.label}`);
    } catch {
      toast.error("Move failed");
      setBoard(prev);
    }
  };

  if (loading && !board) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="animate-spin" size={18} /> Loading board…</div>;
  if (!board) return null;

  return (
    <div data-testid="maintenance-board">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-3">
          {board.statuses.map((col) => (
            <Droppable droppableId={col.key} key={col.key}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps} data-testid={`board-col-${col.key}`}
                  className={`flex-shrink-0 w-56 rounded-xl bg-[#0C0C0C] border ${snapshot.isDraggingOver ? "border-[#F2B705]/50" : "border-white/10"} p-2.5`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className={`text-sm font-bold ${COL_ACCENT[col.key]}`}>{col.label}</span>
                    <span className="text-xs text-white/40 bg-white/5 px-1.5 rounded">{board.counts[col.key]}</span>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {board.columns[col.key].map((c, idx) => (
                      <Draggable draggableId={c.id} index={idx} key={c.id}>
                        {(prov, snap) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            onClick={() => onOpenCard(c.id)} data-testid={`board-card-${c.id}`}
                            className={`rounded-lg bg-[#141414] border-l-4 ${PRIORITY_CLR[c.priority] || "border-l-white/20"} border border-white/10 p-2.5 cursor-pointer hover:border-white/25 ${snap.isDragging ? "shadow-xl ring-1 ring-[#F2B705]/50" : ""}`}>
                            <div className="text-white text-sm font-semibold leading-tight">{c.title}</div>
                            <div className="text-white/40 text-xs mt-0.5">{c.vehicle}</div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px]">
                              {c.priority === "critical" && <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">Critical</span>}
                              {c.priority === "high" && <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">High</span>}
                              {c.parts_total > 0 && (
                                <span className={`flex items-center gap-1 ${c.parts_received === c.parts_total ? "text-emerald-400" : "text-white/50"}`}>
                                  <Package size={11} /> {c.parts_received}/{c.parts_total}
                                </span>
                              )}
                              {c.odometer > 0 && <span className="text-white/40">{c.odometer.toLocaleString()} mi</span>}
                              {(c.actual_cost > 0 || c.estimated_cost > 0) && <span className="text-[#F2B705]">${(c.actual_cost || c.estimated_cost).toLocaleString()}</span>}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {board.columns[col.key].length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-white/20 text-xs text-center py-4 flex flex-col items-center gap-1"><Wrench size={16} /> Empty</div>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
